import { api } from './api'
import { getAll } from './pagination'

export interface AcademicYear {
  id: number
  name: string
  start_date: string
  end_date: string
  is_current: boolean
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface PromotionCriteria {
  id: number
  name: string
  criteria_type: 'attendance' | 'academic' | 'behavior' | 'comprehensive'
  from_class: number
  to_class: number
  from_class_name: string
  to_class_name: string
  from_year_name?: string
  to_year_name?: string
  /** Proposals assessed against these criteria; none means it can be deleted. */
  record_count?: number
  min_attendance_percentage: number
  min_average_grade: number
  max_behavior_incidents: number
  requires_parent_approval: boolean
  requires_principal_approval: boolean
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface StudentPromotion {
  id: number
  student: number
  student_name: string
  student_id: string
  from_class: number
  to_class: number
  from_class_name: string
  to_class_name: string
  academic_year: number
  academic_year_name: string
  criteria: number
  attendance_percentage: number
  average_grade: number
  behavior_incidents: number
  meets_criteria: boolean
  status: 'pending' | 'approved' | 'rejected' | 'completed'
  parent_approved: boolean
  parent_approval_date?: string
  principal_approved: boolean
  principal_approval_date?: string
  approved_by?: number
  approved_by_name?: string
  notes: string
  promotion_date?: string
  created_at: string
  updated_at: string
}

export interface TransferType {
  id: number
  name: string
  description: string
  requires_approval: boolean
  is_active: boolean
  record_count?: number
  created_at: string
}

export interface StudentTransfer {
  id: number
  student: number
  student_name: string
  student_id: string
  transfer_type: number
  transfer_type_name: string
  direction: 'in' | 'out' | 'internal'
  from_school: string
  to_school: string
  from_class?: number
  to_class?: number
  from_class_name?: string
  to_class_name?: string
  reason: string
  request_date: string
  effective_date: string
  status: 'requested' | 'approved' | 'rejected' | 'completed' | 'cancelled'
  parent_approved: boolean
  parent_approval_date?: string
  principal_approved: boolean
  principal_approval_date?: string
  approved_by?: number
  approved_by_name?: string
  transfer_certificate_issued: boolean
  transfer_certificate_date?: string
  documents_attached: any[]
  notes: string
  created_at: string
  updated_at: string
}

export interface GraduationCriteria {
  id: number
  name: string
  class_level: number
  class_level_name: string
  class_year_name?: string
  record_count?: number
  min_attendance_percentage: number
  min_average_grade: number
  required_subjects: string[]
  community_service_hours: number
  extracurricular_participation: boolean
  final_exam_passed: boolean
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface StudentGraduation {
  id: number
  student: number
  student_name: string
  student_id: string
  class_level: number
  class_level_name: string
  academic_year: number
  academic_year_name: string
  criteria: number
  attendance_percentage: number
  average_grade: number
  community_service_hours: number
  extracurricular_participation: boolean
  final_exam_passed: boolean
  meets_criteria: boolean
  status: 'eligible' | 'approved' | 'graduated' | 'rejected'
  graduation_date?: string
  ceremony_date?: string
  certificate_number: string
  certificate_issued: boolean
  certificate_issue_date?: string
  principal_approved: boolean
  principal_approval_date?: string
  approved_by?: number
  approved_by_name?: string
  honors: string
  speech_given: boolean
  notes: string
  created_at: string
  updated_at: string
}

export interface LifecycleEvent {
  id: number
  student: number
  student_name: string
  student_id: string
  event_type: 'enrollment' | 'promotion' | 'transfer_in' | 'transfer_out' | 'graduation' | 'withdrawal' | 'suspension' | 'expulsion'
  event_date: string
  academic_year: number
  academic_year_name: string
  description: string
  previous_status: string
  new_status: string
  promotion?: number
  transfer?: number
  graduation?: number
  notes: string
  created_by?: number
  created_by_name?: string
  created_at: string
}

export interface LifecycleReport {
  id: number
  name: string
  report_type: 'promotion_summary' | 'transfer_summary' | 'graduation_summary' | 'lifecycle_overview' | 'academic_year_report'
  academic_year: number
  academic_year_name: string
  class_filter?: number
  class_filter_name?: string
  date_from?: string
  date_to?: string
  file_path: string
  file_size: number
  generated_at: string
  generated_by?: number
  generated_by_name?: string
  total_students: number
  promoted_students: number
  transferred_students: number
  graduated_students: number
}

export interface LifecycleSummary {
  total_students: number
  eligible_for_promotion: number
  pending_promotions: number
  approved_promotions: number
  pending_transfers: number
  completed_transfers: number
  eligible_for_graduation: number
  pending_graduations: number
  completed_graduations: number
  suspended_students: number
  approved_transfers: number
}

/** What the records say about a learner's year. Figures arrive as decimal strings. */
export interface LearnerMeasures {
  attendance_percentage: string
  attendance_days: number
  average_grade: string
  results_counted: number
  behavior_incidents: number
}

export interface PromotionCandidate extends LearnerMeasures {
  id: number
  full_name: string
  student_id: string
  meets_criteria: boolean
  proposal: { id: number; status: StudentPromotion['status']; to_class_name: string } | null
}

export interface PromotionCandidates {
  criteria: PromotionCriteria
  academic_year: number
  academic_year_name: string
  destination_places: number
  learners: PromotionCandidate[]
}

export interface ClassLifecycleSummary {
  class_id: number
  class_name: string
  total_students: number
  eligible_for_promotion: number
  pending_promotions: number
  eligible_for_graduation: number
  pending_graduations: number
  transfer_requests: number
}

export interface BulkPromotionData {
  student_ids: number[]
  from_class_id: number
  to_class_id: number
  academic_year_id: number
  notes?: string
  /** Figures the person reviewed; a learner left out is assessed from the records. */
  measures?: Array<{ student: number; attendance_percentage: number; average_grade: number; behavior_incidents: number }>
}

export interface BulkTransferData {
  student_ids: number[]
  transfer_type_id: number
  direction: 'in' | 'out' | 'internal'
  reason: string
  effective_date: string
  notes?: string
}

export interface BulkGraduationData {
  student_ids: number[]
  class_level_id: number
  academic_year_id: number
  graduation_date: string
  ceremony_date?: string
  notes?: string
}

class LifecycleService {
  // Academic Years
  async getAcademicYears(params?: {
    is_current?: boolean
    is_active?: boolean
    search?: string
    ordering?: string
  }): Promise<AcademicYear[]> {
    return getAll('/students/lifecycle/academic-years/', params)
  }

  async getAcademicYear(id: number): Promise<AcademicYear> {
    const response = await api.get(`/students/lifecycle/academic-years/${id}/`)
    return response.data
  }

  async createAcademicYear(data: Partial<AcademicYear>): Promise<AcademicYear> {
    const response = await api.post('/students/lifecycle/academic-years/', data)
    return response.data
  }

  async updateAcademicYear(id: number, data: Partial<AcademicYear>): Promise<AcademicYear> {
    const response = await api.patch(`/students/lifecycle/academic-years/${id}/`, data)
    return response.data
  }

  async deleteAcademicYear(id: number): Promise<void> {
    await api.delete(`/students/lifecycle/academic-years/${id}/`)
  }

  // Promotion Criteria
  async getPromotionCriteria(params?: {
    from_class?: number
    to_class?: number
    is_active?: boolean
    search?: string
    ordering?: string
  }): Promise<PromotionCriteria[]> {
    return getAll('/students/lifecycle/promotion-criteria/', params)
  }

  async getPromotionCriteriaDetail(id: number): Promise<PromotionCriteria> {
    const response = await api.get(`/students/lifecycle/promotion-criteria/${id}/`)
    return response.data
  }

  async createPromotionCriteria(data: Partial<PromotionCriteria>): Promise<PromotionCriteria> {
    const response = await api.post('/students/lifecycle/promotion-criteria/', data)
    return response.data
  }

  async updatePromotionCriteria(id: number, data: Partial<PromotionCriteria>): Promise<PromotionCriteria> {
    const response = await api.patch(`/students/lifecycle/promotion-criteria/${id}/`, data)
    return response.data
  }

  async deletePromotionCriteria(id: number): Promise<void> {
    await api.delete(`/students/lifecycle/promotion-criteria/${id}/`)
  }

  // Student Promotions
  async getStudentPromotions(params?: {
    student?: number
    from_class?: number
    to_class?: number
    academic_year?: number
    status?: string
    meets_criteria?: boolean
    search?: string
    ordering?: string
  }): Promise<StudentPromotion[]> {
    return getAll('/students/lifecycle/promotions/', params)
  }

  async getStudentPromotionDetail(id: number): Promise<StudentPromotion> {
    const response = await api.get(`/students/lifecycle/promotions/${id}/`)
    return response.data
  }

  async createStudentPromotion(data: Partial<StudentPromotion>): Promise<StudentPromotion> {
    const response = await api.post('/students/lifecycle/promotions/', data)
    return response.data
  }

  async updateStudentPromotion(id: number, data: Partial<StudentPromotion>): Promise<StudentPromotion> {
    const response = await api.patch(`/students/lifecycle/promotions/${id}/`, data)
    return response.data
  }

  async deleteStudentPromotion(id: number): Promise<void> {
    await api.delete(`/students/lifecycle/promotions/${id}/`)
  }

  async bulkPromotion(data: BulkPromotionData): Promise<{ message: string; promotions: StudentPromotion[] }> {
    const response = await api.post('/students/lifecycle/promotions/bulk/', data)
    return response.data
  }

  async getPromotionCandidates(criteriaId: number): Promise<PromotionCandidates> {
    return (await api.get(`/students/lifecycle/promotion-criteria/${criteriaId}/candidates/`)).data
  }

  async getStudentMeasures(studentId: number, academicYear?: number): Promise<LearnerMeasures & { academic_year: number; academic_year_name: string }> {
    return (await api.get(`/students/lifecycle/students/${studentId}/measures/`, { params: academicYear ? { academic_year: academicYear } : undefined })).data
  }

  async approvePromotion(promotionId: number): Promise<{ message: string; promotion: StudentPromotion }> {
    const response = await api.post(`/students/lifecycle/promotions/${promotionId}/approve/`)
    return response.data
  }

  async rejectPromotion(promotionId: number, reason: string): Promise<StudentPromotion> {
    return (await api.post(`/students/lifecycle/promotions/${promotionId}/reject/`, { reason })).data
  }

  async getParentPendingPromotions(): Promise<StudentPromotion[]> {
    return (await api.get('/students/lifecycle/parent/promotions/')).data
  }

  async parentApprovePromotion(promotionId: number): Promise<StudentPromotion> {
    return (await api.post(`/students/lifecycle/promotions/${promotionId}/parent-approve/`)).data
  }

  async parentRejectPromotion(promotionId: number, reason: string): Promise<StudentPromotion> {
    return (await api.post(`/students/lifecycle/promotions/${promotionId}/parent-reject/`, { reason })).data
  }

  // Transfer Types
  async getTransferTypes(params?: {
    is_active?: boolean
    search?: string
    ordering?: string
  }): Promise<TransferType[]> {
    return getAll('/students/lifecycle/transfer-types/', params)
  }

  async getTransferTypeDetail(id: number): Promise<TransferType> {
    const response = await api.get(`/students/lifecycle/transfer-types/${id}/`)
    return response.data
  }

  async createTransferType(data: Partial<TransferType>): Promise<TransferType> {
    const response = await api.post('/students/lifecycle/transfer-types/', data)
    return response.data
  }

  async updateTransferType(id: number, data: Partial<TransferType>): Promise<TransferType> {
    const response = await api.patch(`/students/lifecycle/transfer-types/${id}/`, data)
    return response.data
  }

  async deleteTransferType(id: number): Promise<void> {
    await api.delete(`/students/lifecycle/transfer-types/${id}/`)
  }

  // Student Transfers
  async getStudentTransfers(params?: {
    student?: number
    transfer_type?: number
    direction?: string
    status?: string
    from_class?: number
    to_class?: number
    search?: string
    ordering?: string
  }): Promise<StudentTransfer[]> {
    return getAll('/students/lifecycle/transfers/', params)
  }

  async getStudentTransferDetail(id: number): Promise<StudentTransfer> {
    const response = await api.get(`/students/lifecycle/transfers/${id}/`)
    return response.data
  }

  async createStudentTransfer(data: Partial<StudentTransfer>): Promise<StudentTransfer> {
    const response = await api.post('/students/lifecycle/transfers/', data)
    return response.data
  }

  async updateStudentTransfer(id: number, data: Partial<StudentTransfer>): Promise<StudentTransfer> {
    const response = await api.patch(`/students/lifecycle/transfers/${id}/`, data)
    return response.data
  }

  async deleteStudentTransfer(id: number): Promise<void> {
    await api.delete(`/students/lifecycle/transfers/${id}/`)
  }

  async approveTransfer(id: number): Promise<StudentTransfer> { return (await api.post(`/students/lifecycle/transfers/${id}/approve/`)).data }
  async rejectTransfer(id: number, reason: string): Promise<StudentTransfer> { return (await api.post(`/students/lifecycle/transfers/${id}/reject/`, { reason })).data }
  async cancelTransfer(id: number, reason: string): Promise<StudentTransfer> { return (await api.post(`/students/lifecycle/transfers/${id}/cancel/`, { reason })).data }
  async completeTransfer(id: number): Promise<StudentTransfer> { return (await api.post(`/students/lifecycle/transfers/${id}/complete/`)).data }
  async downloadTransferCertificate(id: number): Promise<void> {
    const response = await api.get(`/students/lifecycle/transfers/${id}/certificate/`, { responseType: 'blob' })
    const url = URL.createObjectURL(response.data); const link = document.createElement('a'); link.href = url; link.download = `transfer-certificate-${id}.pdf`; link.click(); URL.revokeObjectURL(url)
  }
  async changeStudentStatus(studentId:number,data:{status:'active'|'suspended'|'withdrawn'|'expelled';effective_date:string;reason:string}):Promise<{id:number;status:string}>{ return (await api.post(`/students/lifecycle/students/${studentId}/status/`,data)).data }

  // Graduation Criteria
  async getGraduationCriteria(params?: {
    class_level?: number
    is_active?: boolean
    search?: string
    ordering?: string
  }): Promise<GraduationCriteria[]> {
    return getAll('/students/lifecycle/graduation-criteria/', params)
  }

  async getGraduationCriteriaDetail(id: number): Promise<GraduationCriteria> {
    const response = await api.get(`/students/lifecycle/graduation-criteria/${id}/`)
    return response.data
  }

  async createGraduationCriteria(data: Partial<GraduationCriteria>): Promise<GraduationCriteria> {
    const response = await api.post('/students/lifecycle/graduation-criteria/', data)
    return response.data
  }

  async updateGraduationCriteria(id: number, data: Partial<GraduationCriteria>): Promise<GraduationCriteria> {
    const response = await api.patch(`/students/lifecycle/graduation-criteria/${id}/`, data)
    return response.data
  }

  async deleteGraduationCriteria(id: number): Promise<void> {
    await api.delete(`/students/lifecycle/graduation-criteria/${id}/`)
  }

  // Student Graduations
  async getStudentGraduations(params?: {
    student?: number
    class_level?: number
    academic_year?: number
    status?: string
    meets_criteria?: boolean
    search?: string
    ordering?: string
  }): Promise<StudentGraduation[]> {
    return getAll('/students/lifecycle/graduations/', params)
  }

  async getStudentGraduationDetail(id: number): Promise<StudentGraduation> {
    const response = await api.get(`/students/lifecycle/graduations/${id}/`)
    return response.data
  }

  async createStudentGraduation(data: Partial<StudentGraduation>): Promise<StudentGraduation> {
    const response = await api.post('/students/lifecycle/graduations/', data)
    return response.data
  }

  async updateStudentGraduation(id: number, data: Partial<StudentGraduation>): Promise<StudentGraduation> {
    const response = await api.patch(`/students/lifecycle/graduations/${id}/`, data)
    return response.data
  }

  async deleteStudentGraduation(id: number): Promise<void> {
    await api.delete(`/students/lifecycle/graduations/${id}/`)
  }

  async approveGraduation(graduationId: number): Promise<{ message: string; graduation: StudentGraduation }> {
    const response = await api.post(`/students/lifecycle/graduations/${graduationId}/approve/`)
    return response.data
  }

  async rejectGraduation(graduationId: number, reason: string): Promise<StudentGraduation> {
    return (await api.post(`/students/lifecycle/graduations/${graduationId}/reject/`, { reason })).data
  }

  async downloadGraduationCertificate(graduationId: number): Promise<void> {
    const response = await api.get(`/students/lifecycle/graduations/${graduationId}/certificate/`, { responseType: 'blob' })
    const url = URL.createObjectURL(response.data); const link = document.createElement('a'); link.href = url; link.download = `graduation-certificate-${graduationId}.pdf`; link.click(); URL.revokeObjectURL(url)
  }

  // Lifecycle Events
  async getLifecycleEvents(params?: {
    student?: number
    event_type?: string
    academic_year?: number
    search?: string
    ordering?: string
  }): Promise<LifecycleEvent[]> {
    return getAll('/students/lifecycle/events/', params)
  }

  // Lifecycle Reports
  async getLifecycleReports(params?: {
    report_type?: string
    academic_year?: number
    class_filter?: number
    search?: string
    ordering?: string
  }): Promise<LifecycleReport[]> {
    return getAll('/students/lifecycle/reports/', params)
  }

  async createLifecycleReport(data: Partial<LifecycleReport>): Promise<LifecycleReport> {
    const response = await api.post('/students/lifecycle/reports/', data)
    return response.data
  }

  // Analytics
  async getLifecycleSummary(params?: {
    academic_year_id?: number
    class_id?: number
  }): Promise<LifecycleSummary> {
    const response = await api.get('/students/lifecycle/summary/', { params })
    return response.data
  }

  async getClassLifecycleSummary(params?: {
    academic_year_id?: number
  }): Promise<ClassLifecycleSummary[]> {
    const response = await api.get('/students/lifecycle/class-summary/', { params })
    return response.data
  }

  async getStudentLifecycleHistory(studentId: number): Promise<{
    student: any
    events: LifecycleEvent[]
    promotions: StudentPromotion[]
    transfers: StudentTransfer[]
    graduations: StudentGraduation[]
  }> {
    const response = await api.get(`/students/lifecycle/students/${studentId}/history/`)
    return response.data
  }
}

export const lifecycleService = new LifecycleService()
